import localforage from 'localforage';
import { encryptPayload, decryptPayload } from './encryptionKey';

// Create stores for master data collections
const masterStores = {
    brands: localforage.createInstance({
        name: 'tauris-master-data',
        storeName: 'master_brands',
    }),
    categories: localforage.createInstance({
        name: 'tauris-master-data',
        storeName: 'master_categories',
    }),
    products: localforage.createInstance({
        name: 'tauris-master-data',
        storeName: 'master_products',
    }),
    suppliers: localforage.createInstance({
        name: 'tauris-master-data',
        storeName: 'master_suppliers',
    }),
    customers: localforage.createInstance({
        name: 'tauris-master-data',
        storeName: 'master_customers',
    }),
    invoices: localforage.createInstance({
        name: 'tauris-master-data',
        storeName: 'master_invoices',
    }),
    receipts: localforage.createInstance({
        name: 'tauris-master-data',
        storeName: 'master_receipts',
    }),
    movements: localforage.createInstance({
        name: 'tauris-master-data',
        storeName: 'master_movements',
    }),
    purchases: localforage.createInstance({
        name: 'tauris-master-data',
        storeName: 'master_purchases',
    }),
    sales: localforage.createInstance({
        name: 'tauris-master-data',
        storeName: 'master_sales',
    }),
    expenses: localforage.createInstance({
        name: 'tauris-master-data',
        storeName: 'master_expenses',
    }),
    accounts: localforage.createInstance({
        name: 'tauris-master-data',
        storeName: 'master_accounts',
    }),
    dashboard: localforage.createInstance({
        name: 'tauris-master-data',
        storeName: 'master_dashboard',
    }),
    syncMetadata: localforage.createInstance({
        name: 'tauris-master-data',
        storeName: 'sync_metadata',
    }),
};

const encrypt = (data) => encryptPayload(data);
const decrypt = (cipher) => decryptPayload(cipher);

/**
 * Get all records from a master data collection
 * @param {string} collection - Collection name (brands, products, etc.)
 * @returns {Promise<Array>} Array of records
 */
export const getLocalData = async (collection) => {
    if (!collection) {
        console.warn('getLocalData: collection name is required');
        return [];
    }
    const store = masterStores[collection];
    if (!store) {
        console.warn(`Unknown collection: ${collection}`);
        return [];
    }

    const records = [];
    await store.iterate((value) => {
        const decoded = decrypt(value);
        if (decoded) {
            records.push(decoded);
        }
    });

    // Sort by name if available, otherwise by _id
    return records.sort((a, b) => {
        const nameA = (a.name || a._id || '').toLowerCase();
        const nameB = (b.name || b._id || '').toLowerCase();
        return nameA.localeCompare(nameB);
    });
};

/**
 * Save/update records in bulk to a master data collection.
 * Upserts by _id and removes keys no longer present — avoids full clear+rewrite.
 * @param {string} collection - Collection name
 * @param {Array} records - Array of records to save
 */
export const saveLocalData = async (collection, records) => {
    const store = masterStores[collection];
    if (!store) {
        console.warn(`Unknown collection: ${collection}`);
        return;
    }

    const incoming = Array.isArray(records) ? records : [];
    const incomingIds = new Set(
        incoming.map((r) => (r && r._id != null ? String(r._id) : null)).filter(Boolean)
    );

    // Remove stale local keys not in the latest server snapshot
    const keysToRemove = [];
    await store.iterate((_value, key) => {
        if (!incomingIds.has(String(key))) {
            keysToRemove.push(key);
        }
    });
    await Promise.all(keysToRemove.map((key) => store.removeItem(key)));

    // Upsert incoming records
    for (const record of incoming) {
        if (record._id) {
            await store.setItem(record._id, encrypt(record));
        }
    }

    await updateSyncTimestamp(collection);
};

/**
 * Get last sync timestamp for a collection
 * @param {string} collection - Collection name
 * @returns {Promise<Date|null>} Last sync time or null
 */
export const getLastSyncTime = async (collection) => {
    if (!collection) return null;
    const metadata = masterStores.syncMetadata;
    const raw = await metadata.getItem(collection);
    const data = decrypt(raw);
    return data?.lastSync ? new Date(data.lastSync) : null;
};

/**
 * Update sync timestamp for a collection
 * @param {string} collection - Collection name
 */
export const updateSyncTimestamp = async (collection) => {
    const metadata = masterStores.syncMetadata;
    await metadata.setItem(collection, encrypt({
        lastSync: new Date().toISOString(),
        collection,
    }));
};

/**
 * Clear all master data (useful for logout)
 */
export const clearAllMasterData = async () => {
    const promises = Object.values(masterStores).map(store => store.clear());
    await Promise.all(promises);
};

/**
 * Get a single record by ID from a collection
 * @param {string} collection - Collection name
 * @param {string} id - Record ID
 * @returns {Promise<Object|null>} Record or null
 */
export const getLocalRecord = async (collection, id) => {
    if (!collection || !id) return null;
    const store = masterStores[collection];
    if (!store) return null;

    const raw = await store.getItem(id);
    return decrypt(raw);
};

/**
 * Update a single record in a collection
 * @param {string} collection - Collection name
 * @param {Object} record - Record to update
 */
export const updateLocalRecord = async (collection, record) => {
    if (!collection || !record || !record._id) return;
    const store = masterStores[collection];
    if (!store) return;

    await store.setItem(record._id, encrypt(record));
};

/**
 * Remove a single record from a master data collection (e.g. after soft-delete).
 * @param {string} collection - Collection name
 * @param {string|ObjectId} id - Record _id
 */
export const removeLocalRecord = async (collection, id) => {
    if (!collection || id == null) return;
    const store = masterStores[collection];
    if (!store) return;
    const key = String(id);
    await store.removeItem(key);
    // Some older writes may have used the raw id type as key
    try {
        await store.removeItem(id);
    } catch {
        /* ignore */
    }
};
